/**
 * src/i18n/index.ts — vue-i18n 설정. kei.bio(src/i18n/index.js)와 동일한 언어 목록/구조를
 * 그대로 재사용 — 사용자 지시 "kei.bio 언어 구조 사용해주세요"(2026-09-14). localStorage/
 * origin이 kei.bio와 분리돼 있어(다른 서브도메인) 저장값은 공유되지 않지만, 지원 언어
 * 목록·국기·이름·판별 로직은 동일하게 유지한다.
 * 우선순위: localStorage 저장값 > 브라우저 언어 > 기본값(ko)
 */
import { createI18n } from 'vue-i18n'

/** 지원 로케일 목록 — kei.bio와 동일 */
export const SUPPORTED_LOCALES = ['ko', 'en', 'ja', 'zh', 'zh-TW', 'vi', 'th', 'id', 'es', 'fr', 'de', 'pt', 'ru'] as const
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number]

/** 국기 이모지 매핑 */
export const LOCALE_FLAGS: Record<SupportedLocale, string> = {
  ko: '🇰🇷', en: '🇺🇸', ja: '🇯🇵', zh: '🇨🇳', 'zh-TW': '🇹🇼', vi: '🇻🇳',
  th: '🇹🇭', id: '🇮🇩', es: '🇪🇸', fr: '🇫🇷', de: '🇩🇪', pt: '🇧🇷', ru: '🇷🇺'
}

/** 언어명(해당 언어로 표기) */
export const LOCALE_NAMES: Record<SupportedLocale, string> = {
  ko: '한국어', en: 'English', ja: '日本語', zh: '简体中文', 'zh-TW': '繁體中文', vi: 'Tiếng Việt',
  th: 'ภาษาไทย', id: 'Bahasa Indonesia', es: 'Español', fr: 'Français', de: 'Deutsch', pt: 'Português', ru: 'Русский'
}

const LS_KEY = 'meet_locale'

function isSupported(v: string): v is SupportedLocale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(v)
}

/** 브라우저 언어에서 지원 로케일 탐색 (navigator.languages = ['ko-KR', 'en-US', 'en'] 등) */
function detectBrowserLocale(): SupportedLocale {
  const langs = navigator.languages || [navigator.language || 'ko']
  for (const lang of langs) {
    if (isSupported(lang)) return lang
    const short = lang.split('-')[0]
    if (isSupported(short)) return short
  }
  return 'ko'
}

function resolveLocale(): SupportedLocale {
  try {
    const saved = localStorage.getItem(LS_KEY)
    if (saved && isSupported(saved)) return saved
  } catch {
    /* private mode 등 — 브라우저 감지로 폴백 */
  }
  return detectBrowserLocale()
}

export function saveLocale(locale: SupportedLocale) {
  try {
    localStorage.setItem(LS_KEY, locale)
  } catch {
    /* 저장 실패해도 이번 세션 동작에는 지장 없음 */
  }
}

// ── 로케일 메시지 동적 import — 초기 로드는 ko만, 나머지는 setLocale 호출 시 로드 ──
import ko from '../locales/ko.json'

const loadedMessages: Partial<Record<SupportedLocale, typeof ko>> = { ko }

export async function loadLocaleMessages(locale: SupportedLocale) {
  if (loadedMessages[locale]) {
    i18n.global.setLocaleMessage(locale, loadedMessages[locale]!)
    return
  }
  try {
    const msgs = await import(`../locales/${locale}.json`)
    loadedMessages[locale] = msgs.default
    i18n.global.setLocaleMessage(locale, msgs.default)
  } catch {
    console.warn(`[i18n] ${locale}.json 없음 — ko 폴백`)
    i18n.global.setLocaleMessage(locale, loadedMessages.ko!)
  }
}

const initialLocale = resolveLocale()

export const i18n = createI18n({
  legacy: false,
  locale: initialLocale,
  fallbackLocale: 'ko',
  messages: { ko },
  missingWarn: false,
  fallbackWarn: false
})

/**
 * main.ts가 app.mount() 전에 await해야 하는 준비 Promise — 안 그러면 ko 외
 * 언어가 fallbackLocale(ko)로 잠깐 렌더링됐다가 뒤늦게 바뀌는 깜빡임이 생긴다.
 */
export const localeReady: Promise<void> =
  initialLocale === 'ko' ? Promise.resolve() : loadLocaleMessages(initialLocale)

export async function setLocale(locale: SupportedLocale) {
  if (!isSupported(locale)) return
  await loadLocaleMessages(locale)
  ;(i18n.global.locale as { value: SupportedLocale }).value = locale
  saveLocale(locale)
  document.documentElement.lang = locale
}

export function getLocale(): SupportedLocale {
  return (i18n.global.locale as { value: SupportedLocale }).value
}
